"use client";

import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WINDOW_PRESETS, type WindowPreset } from "@/lib/simulator/window";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * WindowPresetSelect — 5-button segmented control with a Custom option.
 *
 * Buttons: 1Y · 5Y · 10Y · ALL · Custom. Clicking Custom exposes two
 * date pickers (start + end) below the segmented control. Each picker is
 * a single-date shadcn Calendar in a Popover, with month + year dropdowns
 * (`captionLayout="dropdown"`) for fast navigation across long histories.
 *
 * Single-date pickers > range mode: range mode in react-day-picker has
 * confusing UX (every click after the first re-anchors the range), and
 * users with multi-decade histories need to jump years quickly, which the
 * single-date dropdown caption supports.
 *
 * State model: the parent owns `value: WindowSelection`, a discriminated
 * union of preset or custom range. Helpers below construct each variant.
 * ────────────────────────────────────────────────────────────────────────── */

export type WindowSelection =
  | { readonly kind: "preset"; readonly preset: WindowPreset }
  | { readonly kind: "custom"; readonly startDate: string; readonly endDate: string };

export const DEFAULT_WINDOW: WindowSelection = { kind: "preset", preset: "ALL" };

export const presetSelection = (preset: WindowPreset): WindowSelection => ({
  kind: "preset",
  preset,
});

export const customSelection = (startDate: string, endDate: string): WindowSelection => ({
  kind: "custom",
  startDate,
  endDate,
});

const isoToDate = (iso: string | undefined): Date | undefined => {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const dateToIso = (d: Date | undefined): string | undefined =>
  d ? d.toISOString().slice(0, 10) : undefined;

const formatDateLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export interface WindowPresetSelectProps {
  value: WindowSelection;
  onChange: (next: WindowSelection) => void;
  /** Bounds the custom date picker can select within (the ticker's available history). */
  minDate: string;
  maxDate: string;
  disabled?: boolean;
}

export function WindowPresetSelect({
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
}: WindowPresetSelectProps) {
  const allOptions: (WindowPreset | "Custom")[] = [...WINDOW_PRESETS, "Custom"];
  const isCustomActive = value.kind === "custom";

  const customStartIso = isCustomActive ? value.startDate : undefined;
  const customEndIso = isCustomActive ? value.endDate : undefined;

  const handlePresetClick = React.useCallback(
    (label: WindowPreset | "Custom") => {
      if (label === "Custom") {
        // First entry into Custom mode: default to the full available range so
        // the chart doesn't go blank. User then tightens via the date pickers.
        onChange(customSelection(minDate, maxDate));
      } else {
        onChange(presetSelection(label));
      }
    },
    [onChange, minDate, maxDate],
  );

  const handleStartChange = React.useCallback(
    (date: Date | undefined) => {
      const iso = dateToIso(date);
      if (!iso) return;
      // Keep start <= end. If user picks a start after the current end, push
      // end out to max-available (or to start, whichever is later).
      const currentEnd = customEndIso ?? maxDate;
      const newEnd = iso > currentEnd ? maxDate : currentEnd;
      onChange(customSelection(iso, newEnd));
    },
    [onChange, customEndIso, maxDate],
  );

  const handleEndChange = React.useCallback(
    (date: Date | undefined) => {
      const iso = dateToIso(date);
      if (!iso) return;
      const currentStart = customStartIso ?? minDate;
      // If user picks an end before the current start, pull start back to min.
      const newStart = iso < currentStart ? minDate : currentStart;
      onChange(customSelection(newStart, iso));
    },
    [onChange, customStartIso, minDate],
  );

  // Year-dropdown bounds for the Calendar caption.
  const minYearDate = isoToDate(minDate);
  const maxYearDate = isoToDate(maxDate);

  return (
    <div className="space-y-2">
      <div
        role="group"
        aria-label="Date range preset"
        className="border-input bg-background flex h-9 gap-1 rounded-md border p-0.5"
      >
        {allOptions.map((opt) => {
          const active =
            opt === "Custom" ? isCustomActive : value.kind === "preset" && value.preset === opt;
          return (
            <button
              key={opt}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              onClick={() => handlePresetClick(opt)}
              className={cn(
                "flex-1 rounded-sm text-xs font-medium tracking-tight tabular-nums transition-colors",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                "disabled:pointer-events-none disabled:opacity-50",
                active
                  ? "bg-stone-200/80 text-zinc-900 shadow-sm"
                  : "text-zinc-600 hover:bg-stone-100 hover:text-zinc-900",
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {isCustomActive ? (
        <div className="space-y-1.5 pt-1">
          <DateField
            label="Start"
            value={customStartIso}
            onChange={handleStartChange}
            minDate={minDate}
            maxDate={maxDate}
            minYearDate={minYearDate}
            maxYearDate={maxYearDate}
            disabled={disabled}
          />
          <DateField
            label="End"
            value={customEndIso}
            onChange={handleEndChange}
            minDate={minDate}
            maxDate={maxDate}
            minYearDate={minYearDate}
            maxYearDate={maxYearDate}
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}

interface DateFieldProps {
  label: string;
  value: string | undefined;
  onChange: (date: Date | undefined) => void;
  minDate: string;
  maxDate: string;
  minYearDate: Date | undefined;
  maxYearDate: Date | undefined;
  disabled?: boolean;
}

function DateField({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  minYearDate,
  maxYearDate,
  disabled,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);
  const selectedDate = isoToDate(value);

  return (
    <div className="flex items-center gap-2">
      <span className="w-10 shrink-0 font-mono text-[10px] tracking-[0.18em] text-zinc-500 uppercase">
        {label}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="border-input bg-background flex h-8 flex-1 items-center gap-2 rounded-md border px-2.5 text-left text-xs text-zinc-700 hover:bg-stone-50 disabled:pointer-events-none disabled:opacity-50"
          >
            <CalendarIcon className="size-3.5 shrink-0 text-zinc-500" aria-hidden />
            <span className="tabular-nums">{value ? formatDateLabel(value) : "Pick a date"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              onChange(date);
              if (date) setOpen(false);
            }}
            captionLayout="dropdown"
            defaultMonth={selectedDate ?? isoToDate(minDate)}
            startMonth={minYearDate}
            endMonth={maxYearDate}
            disabled={(date) => {
              const iso = dateToIso(date);
              if (!iso) return true;
              return iso < minDate || iso > maxDate;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
